/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as R from 'ramda'
import { Mutex } from 'async-mutex'
import * as Sentry from '@sentry/react'
import { ResponseAction } from '@weblite/ts-action/types'
import {
  reqSignUpUser,
  ResSignUpUserPayload,
  SignUpUser,
} from '@weblite/ts-action/client-user'
import { ProfileUpdateDto } from '@weblite/sahab-profile-action/profile'
import {
  ActiveSession,
  GetActiveSessions,
  reqGetActiveSessions,
  reqLogout,
  reqResendCode,
  reqTerminateSessions,
  TerminateSessions,
} from '@weblite/ts-action/auth'
// @ts-ignore
import { dataAuthSignin, dataAuthVerify } from '@weblite/action/auth'
import {
  reqUseReferrerCode,
  UseReferrerCode,
} from '@weblite/ts-action/invitation'
import { httpRequest, httpRequestWithMetadata } from '$setup/http'
import {
  showErrorSnackbar,
  showPhoneNumberIsNotSignedUpSnackbar,
} from '$comp/snackbars'
import { goToAuthSignup, initializeMainApp } from '$logic/router'
import { clientUserStorage } from '$logic/client-user/storage'
import { t } from '$i18n'
import { normalizePhone } from '$func/phone'
import { migrateFromAnonymousSessionToIdentifiedSession } from '$logic/analytics'
import { afterSignUp } from '$hooks/after/signup'
import { connect, request } from '$setup/ws'
import { sendEventToTwa } from '$logic/twaCommunication/twaCommunication.api'
import { updateTwaPermissions } from '$logic/twaCommunication/service'
import { TWA_EVENTS } from '$logic/twaCommunication'
import { getClientUser } from '$logic/client-user'
import { setIsForcedInstallDisabled } from '$logic/client-device'
import { authStorage } from './storage'
import beforeSignIn from '../../hooks/before/signin'
import afterSignIn from '../../hooks/after/signin'
import afterSignOut from '../../hooks/after/signout'
import { IS_IN_ALPHA_SCHOOL } from '$setup/config/env'
import { activeSessionsCache } from './helper'
import { getThrottleDetails, ThrottleFailure } from './throttle'

export type VerificationSession = { phone: string; sessionId: string }
export type SignInFailure = { errorMessage: string }
export type SignInResult = VerificationSession | ThrottleFailure | SignInFailure

const SIGNIN_ERROR_CODES = [
  'INVALID_PHONE',
  'FAILED_DELIVERY',
  'BANNED',
  'NOT_INVITED',
  'NOT_IN_WHITELIST',
  'TOO_MANY_REQUESTS',
]

export const isSignInFailure = (value: unknown): value is SignInFailure =>
  isRecord(value) && typeof value.errorMessage === 'string'

export async function signInUser(inputPhone: string): Promise<SignInResult> {
  const phone = normalizePhone(inputPhone)
  const { response, status, retryAfter } = await httpRequestWithMetadata(
    dataAuthSignin({ phone }),
  )
  const responseError = getResponseError(response)
  const errorCode = getErrorCode(responseError)

  if (status === 429 || errorCode === 'TOO_MANY_REQUESTS') {
    const throttle = getThrottleDetails(
      responseError ?? getHttpThrottleError(response),
      retryAfter,
    )
    if (throttle) return { throttle }

    return getSignInFailure('TOO_MANY_REQUESTS')
  }

  if (responseError) return getSignInFailure(errorCode)
  if (!response || (status && status >= 400)) return getSignInFailure()

  const verificationSession = getVerificationSession(response)
  if (verificationSession) return verificationSession

  Sentry.captureMessage('Unexpected auth signin response', {
    level: 'warning',
    extra: { status, hasResponse: Boolean(response) },
  })
  return getSignInFailure()
}

function getVerificationSession(response: unknown): VerificationSession | null {
  if (!isRecord(response)) return null
  const payload = response.payload
  if (!isRecord(payload)) return null
  const { phone, sessionId } = payload
  return typeof phone === 'string' && typeof sessionId === 'string'
    ? { phone, sessionId }
    : null
}

function getResponseError(response: unknown): unknown {
  if (!isRecord(response)) return null
  const payload = response.payload
  if (isRecord(payload) && payload.error) return payload.error
  if (response.error) return response.error
  return typeof response.code === 'string' ? response : null
}

function getErrorCode(error: unknown): string | undefined {
  return isRecord(error) && typeof error.code === 'string'
    ? error.code
    : undefined
}

function getHttpThrottleError(response: unknown): Record<string, unknown> {
  if (!isRecord(response)) return { code: 'TOO_MANY_REQUESTS' }
  const details = isRecord(response.payload) ? response.payload : response
  return { code: 'TOO_MANY_REQUESTS', message: details }
}

function getSignInFailure(errorCode?: string): SignInFailure {
  const knownErrorCode = SIGNIN_ERROR_CODES.includes(errorCode ?? '')
    ? errorCode
    : undefined
  return {
    errorMessage: knownErrorCode
      ? t(`signin.serverCodes.${knownErrorCode}`)
      : t('serverError'),
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object'
}

export type ResendResult =
  | 'OK'
  | 'NETWORK_ERROR'
  | 'RESEND_FAILED'
  | ThrottleFailure

export async function resendCode(
  phone: string,
  sessionId: string,
): Promise<ResendResult> {
  const response = await httpRequest(reqResendCode({ phone, sessionId }))
  if (!response) return 'NETWORK_ERROR'
  if (response.error) {
    const throttle = getThrottleDetails(response.error)
    return throttle ? { throttle } : 'RESEND_FAILED'
  }
  return 'OK'
}

function loginEAEvent(userId: string) {
  window.gtag?.('event', 'login', { user_id: userId })
}

function signUpEAEvent(userId: string) {
  window.gtag?.('event', 'sign_up', { user_id: userId })
}

export type VerifyError = 'WRONG_CODE' | 'SERVER_ERROR' | 'CLOSE_VERIFICATION'
export type VerifyResult = VerifyError | ThrottleFailure | { token: string }
// eslint-disable-next-line consistent-return

export async function verifyPhone(
  phone: string,
  sessionId: string,
  code: string,
  shouldSignInUser = true,
): Promise<VerifyResult> {
  const response = await httpRequest(dataAuthVerify({ phone, sessionId, code }))

  if (!response) return 'SERVER_ERROR'

  if (response.payload.error) {
    const throttle = getThrottleDetails(response.payload.error)
    if (throttle) return { throttle }

    if (response.payload.error.code === 'MAX_TRIES_REACHED') {
      showErrorSnackbar(t('signin.verification.maxTriesReached'))
      return 'CLOSE_VERIFICATION'
    }
    if (response.payload.error.code === 'SIGNUP_CLOSED') {
      showErrorSnackbar(t('signin.verification.signupClosed'))
      return 'CLOSE_VERIFICATION'
    }

    return response.payload.error.code
  }

  const { token, invited, ...clientUser } = response.payload

  if (shouldSignInUser) {
    const isUserSignedUp = 'id' in clientUser

    authStorage.set({
      token,
      isSignedUp: isUserSignedUp,
      // User Id is needed for sentry
      userId: clientUser?.id,
    } as Auth)

    if (isUserSignedUp) {
      Sentry.setUser(R.pick(['id', 'username'], clientUser))
      loginEAEvent(clientUser.id)
      await beforeSignIn()
      await clientUserStorage.set(clientUser)
      await initializeMainApp()
      await afterSignIn()
      updateTwaPermissions()
      await migrateFromAnonymousSessionToIdentifiedSession()
      analite('USER_SIGNIN')
    } else {
      if (IS_IN_ALPHA_SCHOOL) {
        showPhoneNumberIsNotSignedUpSnackbar()
        return 'CLOSE_VERIFICATION'
      }
      await goToAuthSignup(invited)
    }
  }

  return { token }
}

export async function signUpUser(
  firstname: string,
  lastname: string,
  profileData: ProfileUpdateDto | null = null,
  refCode?: string,
): Promise<void | string> {
  const response: ResponseAction<ResSignUpUserPayload> = await httpRequest(
    reqSignUpUser({ firstname, lastname, profileData, refCode }),
  )
  if (!response) return
  const { payload, error } = response

  const errorCode = error?.code as SignUpUser['ErrorCodes']
  if (errorCode) return errorCode

  const { token, user } = payload
  Sentry.setUser(R.pick(['id', 'username'], user))
  authStorage.set({
    token,
    isSignedUp: true,
    // User Id is needed for sentry
    userId: user.id,
  } as Auth)

  setIsForcedInstallDisabled(true)

  signUpEAEvent(user.id)
  await afterSignUp()
  await beforeSignIn()
  await clientUserStorage.set(user)
  await initializeMainApp()
  await afterSignIn()
  updateTwaPermissions()
  await migrateFromAnonymousSessionToIdentifiedSession()
  analite('USER_SIGNUP')
}

export async function setReferrerCode(code: string) {
  const createdAt = getClientUser()?.createdAt
  return request<UseReferrerCode>(
    reqUseReferrerCode({ code, usedAt: createdAt }),
  )
}

export async function terminateSessions(selectors: string[]) {
  await request<TerminateSessions>(reqTerminateSessions({ selectors }))
  // The list just changed; drop the cache so the next read (in particular the
  // channel device-limit gate, which caches for minutes) sees it.
  activeSessionsCache.clear()
}

export async function logOutUser() {
  const userId = authStorage.get()?.userId
  if (await httpRequest(reqLogout({}))) {
    await sendEventToTwa(TWA_EVENTS.LOGOUT)
    analite('PROFILE_SETTING_LOGOUT', { userId })
    Sentry.setUser(null)
    afterSignOut()
  }
}

export function loginUserFromAndroid(token: string) {
  const auth = authStorage.get()
  authStorage.set({ ...auth, token, isSignedUp: true })
  connect()
}

export function loginUserFromRouter(token: string) {
  if (!token) {
    console.log('Could not log in. Please provide token')
    return
  }
  const auth = authStorage.get()
  authStorage.set({ ...auth, token, isSignedUp: true })
  connect()
}

export function getAuthToken() {
  return authStorage.get()?.token
}

export function isLoggedIn() {
  // return !!(getAuthToken() && authStorage.get()?.userId)
  return (
    !!getAuthToken() &&
    (authStorage.get()?.isSignedUp || authStorage.get()?.userId)
  )
}

const getActiveSessionsMutex = new Mutex()

/**
 * TTL for callers that only need to *gate* on the session count (the channel
 * device limit), which runs on every channel open. The server throttles this
 * request at 10/min, so the previous 6s TTL meant a user browsing between
 * classes could exhaust the quota in under a minute. The cache is cleared by
 * `terminateSessions`, so a longer window can never keep a user locked out
 * after they free up a device.
 */
export const SESSIONS_GATE_CACHE_TTL_SECONDS = 300

export async function getActiveSessions(
  cacheTTLSeconds = 6,
  useStaleOnThrottle = false,
) {
  return getActiveSessionsMutex.runExclusive(async () => {
    let resPayload: GetActiveSessions['Res']
    const cachedRes = activeSessionsCache.get(cacheTTLSeconds)
    if (cachedRes) {
      resPayload = cachedRes
    } else {
      const res = await request<GetActiveSessions>(reqGetActiveSessions({}))
      if (res.error) {
        if (res.error.code !== 'TOO_MANY_REQUESTS') return null

        // Callers that enforce a limit fall back to the last answer we got,
        // however old, so that provoking the throttle cannot be used to slip
        // past the check. `terminateSessions` clears the cache, so a stale
        // value can never keep someone locked out after they free a device.
        const stale = useStaleOnThrottle
          ? activeSessionsCache.getStale()
          : undefined
        if (!stale) return 'TOO_MANY_REQUESTS'
        resPayload = stale
      } else {
        resPayload = res.payload
        activeSessionsCache.set(resPayload)
      }
    }

    const { sessions, selector } = resPayload
    return {
      current: R.find(
        R.propEq('selector', selector),
        sessions,
      ) as ActiveSession,
      others: R.reject(
        R.propEq('selector', selector),
        sessions,
      ) as ActiveSession[],
    }
  })
}
